import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Container, Row} from "reactstrap";
import {OEC_PARTNERS_LOGO} from "./utils";
import {getPlayStoreUrl} from "../../utils";
import {useGetPublicCompaniesQuery} from "store/services/publicApi";
import {logosFromCompanies, ListedCompany} from "./partnershipLogos";


export const StepsSection = () => {
    const steps = [
        {title: 'Download the mobile app', desc: 'Download our mobile app from play store or app store.'},
        {title: 'Register Account', desc: 'Create your account and start your journey.'},
        {title: 'Complete your profile', desc: 'Let employers find you by providing professional profile.'},
        {title: 'Job Search', desc: 'Browse through available roles.'},
        {title: 'Apply Jobs', desc: 'One click application to your dream job.'}
    ];

    return (
        <section className="py-7">
            <Container>
                <div className="text-center mb-5">
                    <span className="badge badge-pill badge-primary mb-3">Workflow</span>
                    <h2 className="display-3 font-weight-bold">Making Your Job Search Easy</h2>
                </div>
                <Row>
                    {steps.map((step, value) => (
                        <Col key={step.title} md="6" lg="3" className="mb-4">
                            <div className="text-left border-top pt-3 border-light">
                                <small className="text-primary font-weight-bold">0{value+1}</small>
                                <h5 className="font-weight-bold mt-2">{step.title}</h5>
                                <p className="text-sm opacity-7">{step.desc}</p>
                            </div>
                        </Col>
                    ))}
                </Row>

            </Container>
            <Container className={"text-center"}>
                <a href={getPlayStoreUrl()}>
                    <img
                        src="/public/icons/get%20it%20on%20google%20play.svg"
                        className="mr-2"
                        style={{height: "10rem"}}
                        alt="Form"
                    />
                </a>
            </Container>
        </section>
    );
};


const COMPANY_PAGE_SIZE = 9;

const ExtraCompanyPage = ({
    skip,
    onCompanies,
}: {
    skip: number;
    onCompanies: (skip: number, companies: ListedCompany[]) => void;
}) => {
    const {data} = useGetPublicCompaniesQuery({skip});

    useEffect(() => {
        if (data?.companies) onCompanies(skip, data.companies);
    }, [data, onCompanies, skip]);

    return null;
};

const PartnerLogoCarousel = ({logos}: {logos: {name: string; src: string}[]}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(1);
    const [paused, setPaused] = useState(false);

    const metrics = useCallback(() => {
        const track = trackRef.current;
        const card = track?.querySelector(".oec-partner-logo");
        if (!track || !(card instanceof HTMLElement) || logos.length === 0) {
            return null;
        }
        const styles = getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 16;
        const cardWidth = card.getBoundingClientRect().width + gap;
        const visible = Math.max(1, Math.round(track.clientWidth / cardWidth));
        return {
            track,
            cardWidth,
            visible,
            pages: Math.max(1, Math.ceil(logos.length / visible)),
        };
    }, [logos.length]);

    const syncPage = useCallback(() => {
        const next = metrics();
        if (!next) {
            return;
        }
        const index = Math.round(next.track.scrollLeft / (next.cardWidth * next.visible));
        setPageCount(next.pages);
        setPage(Math.min(Math.max(0, index), next.pages - 1));
    }, [metrics]);

    const goTo = useCallback((nextPage: number) => {
        const next = metrics();
        if (!next) {
            return;
        }
        const wrapped = ((nextPage % next.pages) + next.pages) % next.pages;
        next.track.scrollTo({left: wrapped * next.cardWidth * next.visible, behavior: "smooth"});
    }, [metrics]);

    useEffect(() => {
        syncPage();
        window.addEventListener("resize", syncPage);
        return () => window.removeEventListener("resize", syncPage);
    }, [syncPage]);

    useEffect(() => {
        if (paused || pageCount <= 1) {
            return;
        }
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduceMotion) {
            return;
        }
        const timer = window.setInterval(() => goTo(page + 1), 4500);
        return () => window.clearInterval(timer);
    }, [goTo, page, pageCount, paused]);

    if (logos.length === 0) {
        return null;
    }

    return (
        <div
            className="oec-partner-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div
                ref={trackRef}
                className="oec-partner-track"
                onScroll={syncPage}
                tabIndex={0}
                aria-label="Partner logos"
            >
                {logos.map((partner) => (
                    <figure key={`${partner.name}-${partner.src}`} className="oec-partner-logo">
                        <img src={partner.src} alt={partner.name} loading="lazy"/>
                    </figure>
                ))}
            </div>
            {pageCount > 1 && (
                <div className="oec-partner-controls">
                    <button
                        type="button"
                        className="oec-partner-arrow"
                        aria-label="Previous partner logos"
                        onClick={() => goTo(page - 1)}
                    >
                        <i className="fas fa-chevron-left" aria-hidden="true"/>
                    </button>
                    <div className="oec-partner-dots" role="tablist" aria-label="Partner logo pages">
                        {Array.from({length: pageCount}, (_, index) => (
                            <button
                                key={index}
                                type="button"
                                className={index === page ? "is-active" : undefined}
                                aria-label={`Show partner logos ${index + 1}`}
                                aria-current={index === page ? "true" : undefined}
                                onClick={() => goTo(index)}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        className="oec-partner-arrow"
                        aria-label="Next partner logos"
                        onClick={() => goTo(page + 1)}
                    >
                        <i className="fas fa-chevron-right" aria-hidden="true"/>
                    </button>
                </div>
            )}
        </div>
    );
};

export const PartnerSponsorship = () => {
    const {data} = useGetPublicCompaniesQuery({skip: 0});
    const total = data?.totalCount ?? 0;
    const [laterPages, setLaterPages] = useState<Record<number, ListedCompany[]>>({});
    const rememberPage = useCallback((skip: number, companies: ListedCompany[]) => {
        setLaterPages((current) => ({...current, [skip]: companies}));
    }, []);
    const laterSkips = useMemo(() => {
        const skips: number[] = [];
        for (let skip = COMPANY_PAGE_SIZE; skip < total; skip += COMPANY_PAGE_SIZE) skips.push(skip);
        return skips;
    }, [total]);
    const logos = useMemo(() => {
        const companies = [
            ...(data?.companies || []),
            ...laterSkips.flatMap((skip) => laterPages[skip] || []),
        ];
        return [
            ...OEC_PARTNERS_LOGO.map((partner) => ({name: partner.name, src: partner.src})),
            ...logosFromCompanies(companies),
        ];
    }, [data, laterPages, laterSkips]);

    return (
        <section className="oec-partners">
            {laterSkips.map((skip) => (
                <ExtraCompanyPage key={skip} skip={skip} onCompanies={rememberPage}/>
            ))}
            <div className="oec-section-inner">
                <p className="oec-partners-kicker">In partnership with</p>
                <PartnerLogoCarousel logos={logos}/>
            </div>
        </section>
    );
};